import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import { useAuth } from '../../contexts/AuthContext';
import SEOHead from '../../components/SEOHead';
import { generateBreadcrumbStructuredData } from '../../utils/seoUtils';
import Icon from '../../components/AppIcon';
import Footer from '../../components/Footer';



const TermsOfService = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  useEffect(() => {
    window?.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'Terms of Service', path: '/terms-of-service' }
  ];

  const breadcrumbData = generateBreadcrumbStructuredData(breadcrumbs);

  return (
    <>
      <SEOHead
        pageType="terms"
        title="Terms of Service | ParkTrip AI"
        description="Read the Terms of Service for ParkTrip Planner AI including user agreements, service usage, and legal information."
        canonicalUrl="https://parktripplannerai.com/terms-of-service"
        structuredData={breadcrumbData}
      />

      <div className="min-h-screen bg-background flex flex-col">
        <Header 
          isAuthenticated={isAuthenticated}
          user={user}
          onLogout={() => {}}
        />
        
        <main className="flex-1 py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Breadcrumb */}
            <div className="flex items-center space-x-2 text-sm mb-8">
              <Link to="/" className="text-muted hover:text-foreground transition-organic flex items-center">
                <Icon name="Home" size={16} className="mr-1" />
                Home
              </Link>
              <Icon name="ChevronRight" size={16} className="text-muted" />
              <span className="text-foreground font-medium">Terms & Conditions</span>
            </div>

            {/* Header */}
            <div className="mb-8">
              <h1 className="font-heading font-heading-bold text-4xl text-foreground mb-2">
                Terms & Conditions
              </h1>
              <p className="font-body text-muted">Last Updated: November 2025</p>
            </div>

            {/* Content */}
            <div className="bg-card rounded-organic-lg p-8 shadow-sm">
              <div className="prose prose-slate max-w-none">
                <p className="font-body text-base text-muted-foreground leading-relaxed">
                  Welcome to ParkTrip Planner AI ("ParkTrip Planner AI," "we," "our," or "us"). These Terms & Conditions ("Terms") govern your access to and use of our website, tools, trip planning features, and any related services (collectively, the "Service"). By using ParkTrip Planner AI, you agree to these Terms.
                </p>
                <p className="font-body text-base text-muted-foreground leading-relaxed">
                  If you do not agree, you may not use the Service.
                </p>

                {/* Section 1 */}
                <section className="mb-8">
                  <h2 className="font-heading font-heading-bold text-2xl text-foreground mb-4">1. Eligibility</h2>
                  <p className="font-body text-foreground">
                    You must be at least 13 years old to use the Service. If you are under 18, you may only use the Service with permission from a parent or guardian.
                  </p>
                </section>

                {/* Section 2 */}
                <section className="mb-8">
                  <h2 className="font-heading font-heading-bold text-2xl text-foreground mb-4">2. Your Use of the Service</h2>
                  <p className="font-body text-foreground mb-3">You agree not to:</p>
                  <ul className="list-disc pl-6 space-y-2 font-body text-foreground">
                    <li>Use the Service for any unlawful or harmful purpose</li>
                    <li>Attempt to access areas of the Service you are not authorized to use</li>
                    <li>Copy, modify, or reverse-engineer the Service</li>
                    <li>Interfere with servers, networks, or security features</li>
                  </ul>
                  <p className="font-body text-foreground mt-3">
                    We may suspend or terminate your access at any time for violating these Terms.
                  </p>
                </section>

                {/* Section 3 */}
                <section className="mb-8">
                  <h2 className="font-heading font-heading-bold text-2xl text-foreground mb-4">3. Account Registration</h2>
                  <p className="font-body text-foreground mb-3">If the Service allows account creation:</p>
                  <ul className="list-disc pl-6 space-y-2 font-body text-foreground">
                    <li>You must provide accurate information.</li>
                    <li>You are responsible for keeping your login credentials secure.</li>
                    <li>You are responsible for all activity under your account.</li>
                    <li>We may reject or remove accounts at our discretion.</li>
                  </ul>
                </section>

                {/* Section 4 */}
                <section className="mb-8">
                  <h2 className="font-heading font-heading-bold text-2xl text-foreground mb-4">4. User Content</h2>
                  <p className="font-body text-foreground mb-3">
                    You may upload or enter information such as trip plans, notes, saved locations, and other personal content ("User Content"). You retain ownership of your User Content.
                  </p>
                  <p className="font-body text-foreground mb-3">
                    By using the Service, you grant us a non-exclusive license to store, process, and display your User Content solely for the purpose of operating the Service.
                  </p>
                  <p className="font-body text-foreground">
                    We do not claim ownership of your photos or trip details.
                  </p>
                </section>

                {/* Section 5 */}
                <section className="mb-8">
                  <h2 className="font-heading font-heading-bold text-2xl text-foreground mb-4">5. AI-Generated Content</h2>
                  <p className="font-body text-base text-muted-foreground leading-relaxed">
                    ParkTrip Planner AI uses AI to generate trip itineraries, suggestions, and recommendations. You understand and agree that:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 font-body text-foreground">
                    <li>AI outputs may contain errors or omissions</li>
                    <li>You should verify park conditions, closures, and safety information from official sources</li>
                    <li>We are not responsible for decisions made based on AI-generated content</li>
                    <li>The Service is for informational and entertainment purposes only.</li>
                  </ul>
                </section>

                {/* Section 6 */}
                <section className="mb-8">
                  <h2 className="font-heading font-heading-bold text-2xl text-foreground mb-4">6. Third-Party Links</h2>
                  <p className="font-body text-foreground">
                    The Service may include links to state parks, tourism websites, reservation platforms, or other third-party services. We do not control or endorse these external sites.
                  </p>
                </section>

                {/* Section 7 */}
                <section className="mb-8">
                  <h2 className="font-heading font-heading-bold text-2xl text-foreground mb-4">7. Intellectual Property</h2>
                  <p className="font-body text-base text-muted-foreground leading-relaxed">
                    All content, branding, code, design, and intellectual property on the Service (excluding User Content) belong to ParkTrip Planner AI. You may not copy or redistribute any part of the Service without written permission.
                  </p>
                </section>

                {/* Section 8 */}
                <section className="mb-8">
                  <h2 className="font-heading font-heading-bold text-2xl text-foreground mb-4">8. Disclaimers</h2>
                  <p className="font-body text-foreground mb-3">
                    The Service is provided "as is" without warranties of any kind. We do not guarantee that:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 font-body text-foreground">
                    <li>The Service will be uninterrupted or error-free</li>
                    <li>AI recommendations will be accurate</li>
                    <li>Trip conditions (weather, safety, closures, wildlife risk) are up-to-date</li>
                  </ul>
                  <p className="font-body text-foreground mt-3">
                    You assume all risks when exploring parks, trails, and outdoor areas.
                  </p>
                </section>

                {/* Section 9 */}
                <section className="mb-8">
                  <h2 className="font-heading font-heading-bold text-2xl text-foreground mb-4">9. Limitation of Liability</h2>
                  <p className="font-body text-base text-muted-foreground leading-relaxed">
                    To the maximum extent permitted by law, ParkTrip Planner AI is not liable for:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 font-body text-foreground">
                    <li>Accidents, injuries, or incidents occurring during travel or outdoor activities</li>
                    <li>Data loss or unauthorized account access</li>
                    <li>Inaccurate or incomplete trip recommendations</li>
                    <li>Any indirect, incidental, or consequential damages</li>
                  </ul>
                  <p className="font-body text-foreground mt-3">
                    If we are found liable, our total liability will not exceed $100.
                  </p>
                </section>

                {/* Section 10 */}
                <section className="mb-8">
                  <h2 className="font-heading font-heading-bold text-2xl text-foreground mb-4">10. Changes to the Terms</h2>
                  <p className="font-body text-foreground">
                    We may update these Terms at any time. Continued use of the Service indicates acceptance of the updated Terms.
                  </p>
                </section>

                {/* Section 11 */}
                <section className="mb-8">
                  <h2 className="font-heading font-heading-bold text-2xl text-foreground mb-4">11. Governing Law</h2>
                  <p className="font-body text-foreground">
                    These Terms are governed by the laws of the State of Texas, without regard to conflict of law principles.
                  </p>
                </section>

                {/* Section 12 */}
                <section>
                  <h2 className="font-heading font-heading-bold text-2xl text-foreground mb-4">12. Contact Us</h2>
                  <p className="font-body text-foreground">
                    For questions, contact us at{' '}
                    <a href="mailto:support@parktripplannerai.com" className="text-primary hover:text-primary/80 transition-organic">
                      support@parktripplannerai.com
                    </a>
                  </p>
                </section>
              </div>
            </div>

            {/* Back to Home */}
            <div className="mt-8 text-center">
              <Link 
                to="/" 
                className="inline-flex items-center space-x-2 text-primary hover:text-primary/80 transition-organic font-medium"
              >
                <Icon name="ArrowLeft" size={16} />
                <span>Back to Home</span>
              </Link>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default TermsOfService;